// src/pages/CategoryProductsPage.jsx

import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Seo from '../components/Seo';
import CategoryHeader from '../components/CategoryHeader';
import CategoryTags from '../components/CategoryTags';
import SortDropdown from '../components/SortDropdown';
import ProductCard from '../components/ProductCard';
import { getCategorySeoInfo, generateCategoryBreadcrumbs } from '../utils/categorySeoData';
import { generateFullProductPageSchema } from '../utils/schemaGenerators';
import cleanSubcategoryName from '../utils/cleanSubcategoryName';
import '../styles/pages/CategoryProductsPage.scss';
import LogoLoader from '../components/LogoLoader';

const API_URL = process.env.REACT_APP_API_URL;
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.promocionalesenstock.com';

const CategoryProductsPage = () => {
  const { categoryName, subcategoryName } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);

  const initialPageFromQuery = parseInt(query.get('page'), 10) || 1;

  const [products, setProducts] = useState([]);   
  const [page, setPage] = useState(initialPageFromQuery);
  const [hasMore, setHasMore] = useState(true);
  const [sortOrder, setSortOrder] = useState('');
  const [sortByPrice, setSortByPrice] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const activeOrder = sortByPrice ? 'price_asc' : sortOrder;

  useEffect(() => {
    setProducts([]);
    setPage(1);
    setHasMore(true);
    setTotalCount(0);

    const basePath = subcategoryName
      ? `/categorias/${categoryName}/${subcategoryName}`
      : `/categorias/${categoryName}`;
    navigate(`${basePath}?page=1`, { replace: true });

    fetchProducts(1, activeOrder);
  }, [categoryName, subcategoryName, activeOrder]);

  const observer = useRef();
  const lastRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      // Cuando entre el último nodo en vista y haya más páginas:
      if (entries[0].isIntersecting && hasMore && !loading) {
        fetchProducts(page, activeOrder);
      }
    });
    if (node) observer.current.observe(node);
  }, [hasMore, page, activeOrder, loading]);


  const fetchProducts = async (pageNumber, order = '') => {
    if (loading) return;
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const baseEndpoint = subcategoryName
        ? `${API_URL}/categories/${categoryName}/${subcategoryName}/products`
        : `${API_URL}/categories/${categoryName}/products`;

      const response = await axios.get(baseEndpoint, {
        headers: { authorization: token },
        params: {
          page: pageNumber,
          limit: 12,
          order
        }
      });

      const { products: newProducts, totalPages, totalCount: total } = response.data;
      const validProducts = newProducts.filter(
        p => p && p.reference && p.title
      );

      setProducts(prev => [...prev, ...validProducts]);
      setHasMore(pageNumber < totalPages);
      setPage(pageNumber + 1);
      setTotalCount(total || validProducts.length);

      const basePath = subcategoryName
        ? `/categorias/${categoryName}/${subcategoryName}`
        : `/categorias/${categoryName}`;
      navigate(`${basePath}?page=${pageNumber}`, { replace: true });

    } catch (error) {
      console.error('Error fetching category products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (order) => {
    setSortOrder(order);
  };

  const getPageTitle = () => {
    if (products.length > 0) {
      const firstProduct = products[0];
      if (subcategoryName) {
        return cleanSubcategoryName(firstProduct.Subcategories?.[0]?.name) || subcategoryName.replace(/-/g, ' ');
      }
      return firstProduct.Categories?.[0]?.name || categoryName.replace(/-/g, ' ');
    }
    return (subcategoryName || categoryName).replace(/-/g, ' ');
  };

  const chunkArray = (arr, size) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  const filteredProducts = products.filter(
    p => p && p.reference && p.title
  );
  const productChunks = chunkArray(filteredProducts, 4);

  const currentPage = Math.max(initialPageFromQuery, page - 1);
  const totalPages = Math.ceil(totalCount / 12) || 1;
  
  const seoInfo = getCategorySeoInfo(categoryName, subcategoryName);
  
  const pageTitle = currentPage === 1 
    ? `${seoInfo.title} | Promocionales en Stock`
    : `${seoInfo.title} - Página ${currentPage} de ${totalPages} | Promocionales en Stock`;
    
  const pageDescription = currentPage === 1
    ? seoInfo.description
    : `${seoInfo.description} Página ${currentPage} de ${totalPages}. ${totalCount} productos disponibles.`;

  const canonicalBase = subcategoryName
    ? `/categorias/${categoryName}/${subcategoryName}`
    : `/categorias/${categoryName}`;

  const canonical = currentPage === 1 ? canonicalBase : `${canonicalBase}?page=${currentPage}`;

  const prevPageUrl = currentPage > 1
    ? (currentPage === 2 ? canonicalBase : `${canonicalBase}?page=${currentPage - 1}`)
    : null;

  const nextPageUrl = currentPage < totalPages
    ? `${canonicalBase}?page=${currentPage + 1}`
    : null;

  const ogImage = `/images/categorias/${seoInfo.slug}.jpg`;

  const breadcrumbs = generateCategoryBreadcrumbs(categoryName, subcategoryName);

  const jsonLdSchema = generateFullProductPageSchema({
    products: filteredProducts,
    currentPage,
    totalCount,
    pageSize: 12,
    pageUrl: canonical,
    pageTitle: seoInfo.h1 || getPageTitle(),
    pageDescription,
    breadcrumbs
  });

  const additionalMetaTags = [
    { name: 'robots', content: 'index,follow,max-image-preview:large' },
    { name: 'keywords', content: seoInfo.keywords },
    { name: 'author', content: 'Promocionales en Stock' },
    { name: 'geo.region', content: 'CO-DC' },
    { name: 'geo.placename', content: 'Bogotá' },
    { name: 'rating', content: 'general' },
    { name: 'distribution', content: 'global' },
    { name: 'revisit-after', content: '7 days' }
  ];

  return (
    <div className="CategoryProductsPage container">
      {/* == SEO Component con toda la configuración == */}
      <Seo
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonical}
        prevPageUrl={prevPageUrl}
        nextPageUrl={nextPageUrl}
        ogImage={ogImage}
        ogType="website"
        jsonLd={jsonLdSchema}
        additionalMeta={additionalMetaTags}
        siteUrl={SITE_URL}
      />

      {/* Microdata adicional para respaldo */}
      <div 
        itemScope 
        itemType="https://schema.org/CollectionPage" 
        style={{ display: 'none' }}
        aria-hidden="true"
      >
        <meta itemProp="name" content={seoInfo.h1 || getPageTitle()} />
        <meta itemProp="description" content={pageDescription} />
        <meta itemProp="url" content={`${SITE_URL}${canonical}`} />
        <meta itemProp="numberOfItems" content={totalCount} />
      </div>

      <CategoryHeader
        title={seoInfo.h1 || getPageTitle()}
        subtitle={seoInfo.shortDescription || "Descubre nuestra selección de productos cuidadosamente elegidos para brindarte la mejor combinación de funcionalidad y estilo..."}
      />
      
      <CategoryTags />

      {/* Breadcrumbs visibles para UX */}
      {/* <nav aria-label="Breadcrumb" className="breadcrumb-nav">
        <ol className="breadcrumb">
          {breadcrumbs.map((crumb, index) => (
            <li key={index} className="breadcrumb-item">
              {index < breadcrumbs.length - 1 ? (
                <a href={crumb.url}>{crumb.name}</a>
              ) : (
                <span aria-current="page">{crumb.name}</span>
              )}
            </li>
          ))}
        </ol>
      </nav> */}

      <div className="CategoryProductsPage__sort">
        <label htmlFor="sort-by-price-category">
          <input
            id="sort-by-price-category"
            type="checkbox"
            checked={sortByPrice}
            onChange={(e) => setSortByPrice(e.target.checked)}
          />
          {' '}Ordenar: precio de menor a mayor
        </label>
      </div>

      <div className="CategoryProductsPage__sortbar">
        {/* Contador de resultados */}
        <div className="results-count">
          {totalCount > 0 && (
            <span>{totalCount} productos encontrados</span>
          )}
          {currentPage > 1 && (
            <span> - Página {currentPage} de {totalPages}</span>
          )}
        </div>
        {/* <SortDropdown onChange={handleSortChange} /> */}
      </div>

      <div className="CategoryProductsPage__results row">
        <div className="col-12">
          <InfiniteScroll
            dataLength={filteredProducts.length}
            next={() => fetchProducts(page, activeOrder)}
            hasMore={hasMore}
            loader={<LogoLoader />}
            endMessage={
              <p className="Pagination__status" role="status">
                {filteredProducts.length === 0
                  ? 'No hay productos disponibles en esta categoría.'
                  : 'Has visto todos los productos de esta categoría.'}
              </p>
            }
          >
            {productChunks.map((chunk, chunkIndex) => (
              <div key={chunkIndex} className="products-row">
                <div className="row">
                  {chunk.map((product, index) => {
                    const isLast = (chunkIndex * 4 + index + 1) === filteredProducts.length;
                    const productPosition = index + 1 + (currentPage - 1) * 12;
                    
                    return (
                      <div
                        key={`${product.id}-${index}`}
                        ref={isLast ? lastRef : null}
                        className="col-12 col-md-3 mb-4"
                        role="listitem"
                        aria-posinset={productPosition}
                        aria-setsize={totalCount}
                      >
                        <ProductCard 
                          product={product}
                          loading="lazy"
                          priority={index < 4 ? 'high' : 'low'}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </InfiniteScroll>
        </div>
      </div>
    </div>
  );
};

export default CategoryProductsPage;